import classNames from 'classnames';
import React, {
  FC, SelectHTMLAttributes, forwardRef, useState, useRef, useEffect, useMemo,
} from 'react';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { IoIosArrowDown } from 'react-icons/io';
import Tooltip from '../Tooltip';
import './Select.css';

export interface Option {
  label: React.ReactNode;
  value: string | number;
}

interface CustomSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  id: string;
  label?: string;
  options: Option[];
  errMsg?: string;
  tooltipText?: string;
  placeholder?: string;
}

const Select: FC<CustomSelectProps> = forwardRef<
HTMLSelectElement,
CustomSelectProps
>(({
  id, label, errMsg, tooltipText, options, placeholder = 'Seleccionar...', onChange, value,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | number>(
    typeof value === 'string' || typeof value === 'number' ? value : '',
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const ITEM_HEIGHT = 44; // px; keep in sync with item padding
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(240); // default matches max-h-60 (15rem=240px)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (newValue: string | number) => {
    setSelectedValue(newValue);
    if (onChange) {
      // Create a synthetic event to match the native select onChange
      const event = {
        target: { value: newValue },
      } as React.ChangeEvent<HTMLSelectElement>;
      onChange(event);
    }
    setIsOpen(false);
  };

  const selectedOption = options.find((opt) => opt.value === selectedValue);
  const displayValue: React.ReactNode = selectedOption ? selectedOption.label : placeholder;

  const buttonClasses = classNames(
    'w-full h-13 bg-surface-base p-3 border border-border-tertiary rounded-3xl',
    'flex items-center justify-between',
    'focus:outline-none focus:ring-0 focus:ring-primary-500',
    'hover:border-border-secondary',
    'dark:border-gradient-brand',
    'button-shadow',
    'transition-all duration-200',
    {
      'border-red-500 focus:ring-red-400': errMsg,
    },
  );

  // Virtualization math
  const total = options.length;
  const visibleCount = useMemo(
    () => Math.ceil(containerHeight / ITEM_HEIGHT) + 6, // overscan
    [containerHeight],
  );
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - 3);
  const endIndex = Math.min(total, startIndex + visibleCount);
  const topSpacer = startIndex * ITEM_HEIGHT;
  const bottomSpacer = Math.max(0, (total - endIndex) * ITEM_HEIGHT);

  return (
    <div className="form-control flex flex-col w-full relative" ref={dropdownRef}>
      {label && (
        <div className='flex items-center w-full'>
          <label htmlFor={id} className="text-left font-medium mb-1 text-text-secondary">
            {label}
          </label>
          {tooltipText && (
            <Tooltip content={tooltipText}>
              <AiOutlineQuestionCircle className='ml-1 hover:text-text-primary transition-colors' />
            </Tooltip>
          )}
        </div>
      )}
      <button
        type="button"
        className={buttonClasses}
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            // When opening, ensure selected item is visible
            setTimeout(() => {
              if (listRef.current) {
                setContainerHeight(listRef.current.clientHeight || 240);
                const idx = options.findIndex((o) => o.value === selectedValue);
                if (idx >= 0) {
                  listRef.current.scrollTop = idx * ITEM_HEIGHT;
                  setScrollTop(listRef.current.scrollTop);
                }
              }
            }, 0);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className='text-text-primary flex items-center gap-2'>{displayValue}</span>
        <IoIosArrowDown className={`text-text-tertiary transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className={`absolute z-50 ${label ? 'top-20' : 'top-12'} w-full mt-1 bg-surface-base border border-border-secondary rounded-3xl shadow-lg max-h-60 overflow-auto`}
          ref={listRef}
          onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}
        >
          <div style={{ height: topSpacer }} />
          {options.slice(startIndex, endIndex).map((option) => (
            <div
              key={option.value}
              className={classNames(
                'px-4 py-2 cursor-pointer text-text-primary',
                'hover:bg-primary-500 hover:text-white',
                'transition-colors duration-200',
                {
                  'bg-primary-500 text-white': option.value === selectedValue,
                },
              )}
              style={{ height: ITEM_HEIGHT }}
              onClick={() => handleSelect(option.value)}
            >
              <div className="flex items-center gap-2 h-full">{option.label}</div>
            </div>
          ))}
          <div style={{ height: bottomSpacer }} />
        </div>
      )}

      {errMsg && (
        <span className="text-red-500 text-sm mt-1">{errMsg}</span>
      )}
    </div>
  );
});

export default Select;
